import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";

const CartCtx = createContext(null);

export const FREE_BOTTLE_THRESHOLD = 80; // EUR
const STORAGE_KEY = "goldenelia_cart_v1";

const loadInitial = () => {
  try {
    const raw = typeof window !== "undefined" ? window.localStorage.getItem(STORAGE_KEY) : null;
    if (!raw) return { items: [], freeBottle: null };
    const parsed = JSON.parse(raw);
    return {
      items: Array.isArray(parsed.items) ? parsed.items : [],
      freeBottle: parsed.freeBottle || null,
    };
  } catch {
    return { items: [], freeBottle: null };
  }
};

/**
 * Cart item shape:
 * { id, categoryKey, categoryName, productName, size, tier, unitPrice, qty }
 * id = `${categoryKey}-${tier}` (unique per variant)
 */
export const CartProvider = ({ children }) => {
  const initial = loadInitial();
  const [items, setItems] = useState(initial.items);
  const [isOpen, setOpen] = useState(false);
  const [freeBottle, setFreeBottle] = useState(initial.freeBottle);
  const [pulseToken, setPulseToken] = useState(0);
  const [openCheckoutToken, setOpenCheckoutToken] = useState(0);

  // Persist cart on every change
  useEffect(() => {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify({ items, freeBottle }));
    } catch { /* quota or disabled — ignore */ }
  }, [items, freeBottle]);

  const addItem = useCallback((item, addQty = 1) => {
    setItems((prev) => {
      const idx = prev.findIndex((p) => p.id === item.id);
      if (idx >= 0) {
        const next = [...prev];
        next[idx] = { ...next[idx], qty: Math.min(99, next[idx].qty + addQty) };
        return next;
      }
      return [...prev, { ...item, qty: addQty }];
    });
  }, []);

  const removeItem = useCallback((id) => {
    setItems((prev) => prev.filter((p) => p.id !== id));
  }, []);

  const updateQty = useCallback((id, qty) => {
    if (qty <= 0) {
      setItems((prev) => prev.filter((p) => p.id !== id));
      return;
    }
    setItems((prev) =>
      prev.map((p) => (p.id === id ? { ...p, qty: Math.min(99, qty) } : p))
    );
  }, []);

  const clearCart = useCallback(() => {
    setItems([]);
    setFreeBottle(null);
  }, []);

  const subtotal = useMemo(
    () => items.reduce((s, it) => s + it.unitPrice * it.qty, 0),
    [items]
  );

  const totalCount = useMemo(
    () => items.reduce((s, it) => s + it.qty, 0),
    [items]
  );

  const eligibleForFreeGift = subtotal >= FREE_BOTTLE_THRESHOLD;

  // If user becomes ineligible, drop the gift
  if (freeBottle && !eligibleForFreeGift) {
    setFreeBottle(null);
  }

  const value = {
    items,
    addItem,
    removeItem,
    updateQty,
    clearCart,
    subtotal,
    totalCount,
    isOpen,
    openCart: () => setOpen(true),
    closeCart: () => setOpen(false),
    eligibleForFreeGift,
    freeBottle,
    setFreeBottle,
    pulseCart: () => setPulseToken((t) => t + 1),
    pulseToken,
    buyNow: () => {
      setOpen(true);
      setOpenCheckoutToken((t) => t + 1);
    },
    openCheckoutToken,
  };

  return <CartCtx.Provider value={value}>{children}</CartCtx.Provider>;
};

export const useCart = () => {
  const ctx = useContext(CartCtx);
  if (!ctx) throw new Error("useCart must be used within CartProvider");
  return ctx;
};
